/**
 * OverviewTab — Finance parent page's default sub-tab (2026-07-11, batch-B G1).
 *
 * Splits money collected from SPARES vs FULL-TIME players (season fees),
 * plus a rolled-up Expenses card. Every number here comes from data that
 * already exists — nothing is invented:
 *   • Spares:      GET /payments/team/:teamId/finance (byPlayer.totalCollectedCents,
 *                  Stripe + G4 cash), same source SparesTab uses.
 *   • Full Timers: fullTimersData.js — fans out finance/player/:userId calls
 *                  per full-time roster member and sums payment_type='season_fee'
 *                  rows (see that file's header comment for why this is O(N)
 *                  requests instead of one aggregate call).
 *   • Expenses:    GET /teams/:teamId/budget/finance-expenses — a small
 *                  budget.js addition that reads kind='expense' transactions
 *                  WITHOUT requiring the team to have the full Budget module
 *                  turned on (see that route's comment for why).
 *
 * IMPORTANT double-count guard: payments.js's per-player "paid" total sums
 * EVERY payments row for a user regardless of payment_type. A full-time
 * player who has ALSO paid a season fee would otherwise have that season-fee
 * dollar amount swept into the "Spares collected" figure too (since it's
 * technically "a payment by that user"). We can't fix this at the source
 * (payments.js is out of scope for this batch), so instead we exclude any
 * user who IS a current full-timer from the Spares sum here — see
 * `ftUserIds` below. Spare-fee payments are membership-scoped to
 * is_primary=false players anyway, so this exclusion should be a no-op for
 * teams that don't mix the two payment types on the same person.
 */
import { useState, useEffect } from 'react';
import api from '../../services/api.js';
import { loadFullTimersData, fetchMembers, filterFullTimeMembers } from './fullTimersData.js';

const fmtMoney = (cents, currency = 'cad') => {
  const amount = (cents || 0) / 100;
  const ccy = (currency || 'cad').toUpperCase();
  return `${ccy} ${amount.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
};

const linkBtn = {
  textDecoration: 'underline', color: 'var(--ice)', background: 'none',
  border: 'none', cursor: 'pointer', padding: 0, font: 'inherit',
};

export default function OverviewTab({ teamId, paymentEnabled, seasonFeeRequired, onSelectTab }) {
  const [members, setMembers] = useState(undefined); // undefined=loading
  const [spares,  setSpares]  = useState(undefined); // undefined=loading, null=off/error
  const [ft,      setFt]      = useState(undefined);
  const [expenses, setExpenses] = useState(undefined);

  useEffect(() => {
    let cancelled = false;
    fetchMembers(teamId).then(m => { if (!cancelled) setMembers(m); }).catch(() => { if (!cancelled) setMembers([]); });
    return () => { cancelled = true; };
  }, [teamId]);

  useEffect(() => {
    let cancelled = false;
    if (!paymentEnabled) { setSpares(null); return; }
    api.get(`/payments/team/${teamId}/finance`)
      .then(r => { if (!cancelled) setSpares(r.data); })
      .catch(() => { if (!cancelled) setSpares(null); });
    return () => { cancelled = true; };
  }, [teamId, paymentEnabled]);

  useEffect(() => {
    let cancelled = false;
    if (!seasonFeeRequired || members === undefined) { if (!seasonFeeRequired) setFt(null); return; }
    loadFullTimersData(teamId, members)
      .then(d => { if (!cancelled) setFt(d); })
      .catch(() => { if (!cancelled) setFt(null); });
    return () => { cancelled = true; };
  }, [teamId, seasonFeeRequired, members]);

  useEffect(() => {
    let cancelled = false;
    api.get(`/teams/${teamId}/budget/finance-expenses`)
      .then(r => { if (!cancelled) setExpenses(r.data); })
      .catch(() => { if (!cancelled) setExpenses(null); });
    return () => { cancelled = true; };
  }, [teamId]);

  const ftUserIds = new Set(filterFullTimeMembers(members || []).map(m => m.userId));

  const spareCcy = spares ? (Object.keys(spares.totals || {})[0] || spares.currency) : 'cad';
  // See the file-header comment — excludes any current full-timer's rows
  // so a season-fee payment can't double-count into this total.
  const spareCollected = spares
    ? (spares.byPlayer || []).filter(p => !ftUserIds.has(p.userId))
        // 2026-09-17 (per Gilles) — NET to coach (card − refunds − system
        // fees + cash), not gross; matches the Spares tab's headline tile.
        .reduce((s, p) => s + (p.netToCoachCents != null && !p.feeDataMissing ? p.netToCoachCents : (p.totalCollectedCents || 0)), 0)
    : 0;

  const expenseByCcy = {};
  (expenses?.transactions || []).forEach(t => {
    const c = (t.currency || 'cad').toLowerCase();
    expenseByCcy[c] = (expenseByCcy[c] || 0) + (t.amount_cents || 0);
  });
  const primaryExpenseCcy = Object.keys(expenseByCcy)[0] || 'cad';
  const totalExpenseCents = expenseByCcy[primaryExpenseCcy] || 0;
  const ftCount = expenses?.fullTimePlayerCount || 0;

  return (
    <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-5">
      {/* Spares */}
      <div className="card">
        <h3 className="font-semibold text-sm mb-2">Collected from Spares</h3>
        {!paymentEnabled ? (
          <p className="text-xs text-secondary" style={{ lineHeight: 1.5 }}>
            Spare payment collection is off.{' '}
            <button type="button" style={linkBtn} onClick={() => onSelectTab('spares')}>See Spares tab</button>
          </p>
        ) : spares === undefined || members === undefined ? (
          <p className="text-xs text-secondary">Loading…</p>
        ) : spares === null ? (
          <p className="text-xs text-secondary">Couldn't load spare payment data.</p>
        ) : (
          <>
            <div className="font-bold" style={{ fontSize: 22, color: 'var(--goal)', fontFamily: 'var(--font-display)' }}>
              {fmtMoney(spareCollected, spareCcy)}
            </div>
            <p className="text-xs text-secondary mt-1" style={{ lineHeight: 1.5 }}>
              Stripe + cash payments recorded on the Spares tab.{' '}
              <button type="button" style={linkBtn} onClick={() => onSelectTab('spares')}>Full breakdown →</button>
            </p>
          </>
        )}
      </div>

      {/* Full timers */}
      <div className="card">
        <h3 className="font-semibold text-sm mb-2">Collected from Full Timers</h3>
        {!seasonFeeRequired ? (
          <p className="text-xs text-secondary" style={{ lineHeight: 1.5 }}>
            Season Fees (Full-Time Players) aren't enabled for this team — turn it on in
            Team Settings → Pricing to track and collect full-season fees here.
          </p>
        ) : ft === undefined ? (
          <p className="text-xs text-secondary">Loading…</p>
        ) : ft === null ? (
          <p className="text-xs text-secondary">Couldn't load season-fee data.</p>
        ) : (
          <>
            <div className="font-bold" style={{ fontSize: 22, color: 'var(--goal)', fontFamily: 'var(--font-display)' }}>
              {fmtMoney(ft.totalCollectedCents, ft.currency)}
            </div>
            <p className="text-xs text-secondary mt-1" style={{ lineHeight: 1.5 }}>
              {ft.players.length} full-time player{ft.players.length === 1 ? '' : 's'} tracked.{' '}
              <button type="button" style={linkBtn} onClick={() => onSelectTab('fulltimers')}>See Full Timers tab →</button>
            </p>
          </>
        )}
      </div>

      {/* Expenses */}
      <div className="card">
        <h3 className="font-semibold text-sm mb-2">Expenses</h3>
        {expenses === undefined ? (
          <p className="text-xs text-secondary">Loading…</p>
        ) : expenses === null ? (
          <p className="text-xs text-secondary">Couldn't load expenses.</p>
        ) : (
          <>
            <div className="font-bold" style={{ fontSize: 22, color: 'var(--penalty)', fontFamily: 'var(--font-display)' }}>
              {fmtMoney(totalExpenseCents, primaryExpenseCcy)}
            </div>
            {ftCount > 0 && totalExpenseCents > 0 ? (
              <p className="text-xs text-secondary mt-1" style={{ lineHeight: 1.5 }}>
                ≈ {fmtMoney(Math.round(totalExpenseCents / ftCount), primaryExpenseCcy)} / full-time player
                {' '}({ftCount} tracked, goalies excluded)
              </p>
            ) : (
              <p className="text-xs text-secondary mt-1">
                <button type="button" style={linkBtn} onClick={() => onSelectTab('expenses')}>See Expenses tab →</button>
              </p>
            )}
          </>
        )}
      </div>
    </div>
  );
}
